import React from 'react';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';

const TYPE_STYLES = {
  success: {
    border: 'border-emerald-200 dark:border-emerald-800/50',
    icon: CheckCircleIcon,
    iconWrap: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400',
  },
  error: {
    border: 'border-red-200 dark:border-red-800/50',
    icon: XCircleIcon,
    iconWrap: 'bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400',
  },
  warning: {
    border: 'border-amber-200 dark:border-amber-800/50',
    icon: ExclamationTriangleIcon,
    iconWrap: 'bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400',
  },
  info: {
    border: 'border-forma-200 dark:border-forma-800/50',
    icon: InformationCircleIcon,
    iconWrap: 'bg-forma-100 text-forma-700 dark:bg-forma-950/60 dark:text-forma-400',
  },
};

function ThemedToast({ t, message, type }) {
  const style = TYPE_STYLES[type] || TYPE_STYLES.info;
  const Icon = style.icon;

  return (
    <div
      role="status"
      className={`pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border bg-white px-4 py-3.5 shadow-lg shadow-slate-900/10 transition-all dark:bg-slate-900 dark:shadow-black/30 ${style.border} ${
        t.visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <div
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${style.iconWrap}`}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <p className="flex-1 pt-1.5 text-sm font-medium leading-snug text-slate-900 dark:text-slate-50">
        {message}
      </p>
      <button
        type="button"
        onClick={() => toast.dismiss(t.id)}
        className="shrink-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        aria-label="Fermer"
      >
        <span className="text-lg leading-none">&times;</span>
      </button>
    </div>
  );
}

function show(type, message, options = {}) {
  if (!message) return;
  return toast.custom(
    (t) => <ThemedToast t={t} message={message} type={type} />,
    {
      duration: options.duration ?? 4500,
      style: { background: 'transparent', boxShadow: 'none', padding: 0, margin: 0 },
      ...options,
    }
  );
}

/** Smart fallback when migrating from native alert() */
function showFromMessage(message) {
  const text = String(message).toLowerCase();
  if (
    text.includes('erreur') ||
    text.includes('error') ||
    text.includes('échec') ||
    text.includes('fail') ||
    text.includes('impossible')
  ) {
    return show('error', message);
  }
  if (
    text.includes('succès') ||
    text.includes('success') ||
    text.includes('✅') ||
    text.includes('créé') ||
    text.includes('crée') ||
    text.includes('mis à jour') ||
    text.includes('supprimé') ||
    text.includes('enregistré') ||
    text.includes('exporté')
  ) {
    return show('success', message);
  }
  if (text.includes('conflit') || text.includes('conflict') || text.includes('⚠')) {
    return show('warning', message);
  }
  return show('info', message);
}

export const notify = {
  success: (message, options) => show('success', message, options),
  error: (message, options) => show('error', message, options),
  warning: (message, options) => show('warning', message, options),
  info: (message, options) => show('info', message, options),
  show: showFromMessage,
};

export default notify;
